import ConnectionRetry from "./components/ConnectionRetry";
import React, { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { fetchRolePrivileges, useAuth } from "./store/useAuth";
import SiteDataProvider from "./components/editSite/SiteDataProvider";
import ZoneCheckIn from "./pages/ZoneCheckIn";
import ScanBarcodeGun from "./pages/gun-scanner/ScanBarcodeGun";
import EventLogin from "./components/Login";
import TicketCustomizerRoute from "./pages/TicketCustomizerRoute";
import SiteExhibitorCategory from "./components/editSite/exhibitor/SiteExhibitorCategory";
import SiteExhibitorFloorPlan from "./components/editSite/exhibitor/SiteExhibitorFloorPlan";
import SiteExhibitorsList from "./components/editSite/exhibitor/SiteExhibitorsList";
import SiteExhibitorProfilePage from "./components/editSite/exhibitor/siteExhibitorProfilePage/SiteExhibitorProfilePage";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ExhibitorSpace from "./components/editSite/dashboard/exhibitorSpace/ExhibitorSpace";
import { getEventData, getEventId } from "./services/event";
import { useBase } from "./store/useBase";
import { useSite } from "./store/useSite";
import usePwaManifest from "./hooks/usePwaManifest";
import PwaInstallBanner from "./components/PwaInstallBanner";
import { setDashboardContext } from "./utils/dashboardContext";
import { toDashboardPath } from "./utils/dashboardRoutes";
import { matchesPrivilegePath } from "./utils/privilegePaths";
import {
  accessDebug,
  accessDebugError,
  accessDebugTable,
  accessDebugWarn,
} from "./utils/accessDebug";

// Lazy load all components and pages
const EventSignup = lazy(() => import("./components/SignUp"));
const VerifyOtp = lazy(() => import("./components/VerifyOtp"));
const AccessDenied = lazy(() => import("./pages/AccessDenied"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Dashboard1 = lazy(() => import("./pages/Dashboard1"));
const EventInfoPage = lazy(() => import("./pages/EventInfo"));
const Team = lazy(() => import("./pages/Team"));
const AuthGuard = lazy(() => import("./AuthGuard/AuthGuard"));
const RedirectGuard = lazy(() => import("./AuthGuard/RedirectGuard"));
const AcceptInvitation = lazy(() => import("./pages/AcceptInvitations"));
const RejectInvitation = lazy(() => import("./pages/RejectInvitation"));
const Sessions = lazy(() => import("./pages/Sessions"));
const SessionsPage = lazy(() => import("./pages/SessionsPage"));
const AccessManagement = lazy(() => import("./pages/AccessManagement"));
const Cancellation = lazy(() => import("./pages/Cancellation"));

const VerifyTicket = lazy(() => import("./pages/VerifyTicket"));
const AppliedRegistrations = lazy(() => import("./pages/AppliedRegistration"));
const RegistrationImportExport = lazy(
  () => import("./pages/registration/ImportExport")
);
const DashboardContact = lazy(() => import("./pages/DashboardContact"));
const DashboardPreRegGroups = lazy(
  () => import("./pages/DashboardPreRegGroups"),
);
const DashboardAffilate = lazy(() => import("./pages/Affilate"));
const DashboardScan = lazy(() => import("./pages/ScanQRCode"));
const DashboardAnalytics = lazy(() => import("./pages/Analytics"));
const TransactionReport = lazy(() => import("./pages/TransactionReport"));
const SelectedScanPage = lazy(
  () => import("./components/scanQR/SelectedScanPgae"),
);
const WhatsApp = lazy(() => import("./pages/Whatsapp"));
const RsvpEdit = lazy(() => import("./pages/RsvpEdit"));
const EmailSettings = lazy(() => import("./pages/EmailSettings"));
const Communication = lazy(() => import("./pages/Communication"));
const GlobalCommunication = lazy(() => import("./pages/GlobalCommunication"));
const EmailBodyEdit = lazy(
  () => import("./components/editEmail/EmailBodyEdit"),
);
const ScanTicket = lazy(() => import("./pages/ScanTicket"));
const CheckInCheckoutConfirmation = lazy(
  () => import("./pages/CheckInCheckoutConfirmation"),
);
const Subscription = lazy(() => import("./pages/Subscription"));
const Homepage = lazy(() => import("./pages/HomePage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const UnsubscribeEmail = lazy(() => import("./pages/UnsubscribeEmail"));
const VendorVenueWeb = lazy(() => import("./pages/VendorVenueWeb"));
const PaymentStatus = lazy(() => import("./pages/PaymentStatus"));
const PaymentCheckout = lazy(() => import("./pages/PaymentCheckout"));
const Profile = lazy(() => import("./pages/Profile"));
const Speakers = lazy(() => import("./pages/Speakers"));
const OnSpotRegistration = lazy(() => import("./pages/OnSpotRegistration"));
const Sponsors = lazy(() => import("./pages/Sponsors"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const NotFound = lazy(() => import("./pages/PageNotFound"));
const TermsAndCondition = lazy(() => import("./pages/TermsAndCondition"));

const Refunds = lazy(() => import("./pages/RefundPolicy"));
const ContactUsPage = lazy(() => import("./pages/ContactUsPage"));
const AuthenticatedLayout = lazy(() => import("./pages/AuthenticatedLayout"));
const Promotion = lazy(() => import("./pages/Promotion"));
const EventSchedule = lazy(() => import("./components/EventSchedule"));
const ScheduleManagement = lazy(() => import("./pages/ScheduleManagement"));
const HallAnimation = lazy(() => import("./components/HallAnimation"));
const EventDayScreen = lazy(() => import("./pages/EventDayScreen"));
const HallListPage = lazy(() => import("./pages/HallListPage"));
const Hall = lazy(() => import("./pages/HallLayout"));
const BlogsHome = lazy(() => import("./pages/BlogsHome"));
const BlogDetail = lazy(() => import("./components/BlogDetail"));
const EventRegistrationPage = lazy(() => import("./pages/EventRegistration"));
const EventTicketing = lazy(() => import("./pages/EventTicketing"));
const CheckInBadging = lazy(() => import("./pages/CheckInBadging"));
const GetInTouch = lazy(() => import("./pages/GetInTouch"));
const VenueListPage = lazy(() => import("./pages/VenueListPage"));
const LoadTestRunner = lazy(() => import("./pages/servertest"));
const HallDetails = lazy(() => import("./pages/HallDetails"));
const LayoutB = lazy(() => import("./components/LayoutB"));
const ScrollToTop = lazy(() => import("../src/ScrollToTop"));
const RegistrationTicketClass = lazy(
  () => import("./pages/RegistrationTicketClass"),
);
const TicketClassEditorPage = lazy(
  () => import("./pages/TicketClassEditorPage"),
);
const MasterTicketClassEditorPage = lazy(
  () => import("./pages/MasterTicketClassEditorPage"),
);
const ExhibitorTicketClass = lazy(() => import("./pages/ExhibitorTicketClass"));
const ExhibitorTicketRegistrations = lazy(
  () => import("./pages/ExhibitorTicketRegistrations"),
);
const Notifications = lazy(() => import("./pages/Notifications"));
const Discounts = lazy(() => import("./pages/Discounts"));
const TeamTicketClass = lazy(() => import("./pages/TeamTicketClass"));
const TeamTicketRegistrations = lazy(
  () => import("./pages/TeamTicketRegistrations"),
);
const ScanQR = lazy(() => import("./pages/ScanQR"));
const ScanBarcode = lazy(() => import("./pages/ScanBarcode"));
const MembersRegistration = lazy(() => import("./pages/MembersRegistration"));
const PaymentSettings = lazy(() => import("./pages/payment/PaymentSettings"));
const PaymentGatewayDetails = lazy(
  () => import("./pages/payment/PaymentGatewayDetails"),
);

// Exhibitor Pages
const ExhibitorCategory = lazy(
  () => import("./pages/exhibitor/ExhibitorCategory"),
);
const ExhibitorRequests = lazy(
  () => import("./pages/exhibitor/ExhibitorRequests"),
);
const FloorPlan = lazy(() => import("./pages/exhibitor/FloorPlan"));
const Booths = lazy(() => import("./pages/exhibitor/Booths"));
const Exhibitors = lazy(() => import("./pages/exhibitor/Exhibitors"));
const BoothMembers = lazy(() => import("./pages/exhibitor/BoothMembers"));
const ExhibitorSummary = lazy(
  () => import("./pages/exhibitor/ExhibitorSummary"),
);
const ExhibitorSettings = lazy(
  () => import("./pages/exhibitor/ExhibitorSettings"),
);
const ExhibitorFormManager = lazy(
  () => import("./pages/exhibitor/ExhibitorRequestForm"),
);

const RsvpAccommodation = lazy(() => import("./pages/rsvp/Accommodation"));
const RsvpTransportation = lazy(() => import("./pages/rsvp/Transportation"));
const RsvpImportExport = lazy(() => import("./pages/rsvp/ImportExport"));
const RsvpAllotments = lazy(() => import("./pages/rsvp/Allotments"));

const RsvpFlowRedirect = ({ edit = false }) => {
  const { slug, flowId } = useParams();
  const editPath = edit ? `/edit${flowId ? `/${flowId}` : ""}` : "";
  return (
    <Navigate
      to={`${slug ? `/dashboard/${slug}` : ""}/whatsapp/broadcast${editPath}`}
      replace
    />
  );
};

const RsvpRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`${slug ? `/dashboard/${slug}` : ""}/rsvp/accommodation`} replace />;
};

const EditSite = lazy(() => import("./pages/EditSite"));
const SiteDashboard = lazy(
  () => import("./components/editSite/dashboard/Dashboard"),
);
const SiteAgenda = lazy(() => import("./components/editSite/agenda/Agenda"));
const SiteTerms = lazy(() => import("./components/editSite/terms/Terms"));
const SiteAbout = lazy(() => import("./components/editSite/about/About"));
const SiteContact = lazy(() => import("./components/editSite/contact/Contact"));
const SiteTickets = lazy(() => import("./components/editSite/tickets/Ticket"));
const Forms = lazy(() => import("./pages/Forms"));
const CreateFormPage = lazy(() => import("./pages/CreateFormPage"));
const PublicCustomForm = lazy(() => import("./components/forms/PublicCustomForm"));

// Exhibitor Space Tab Components
const OverviewTab = lazy(() => import("./components/siteExhibitors/OverviewTab"));
const BoothMembersTab = lazy(() => import("./components/siteExhibitors/BoothMembersTab"));
const LeadsTab = lazy(
  () => import("./components/siteExhibitors/leadsTab/LeadsTab"),
);
const BoothMaterialsTab = lazy(() => import("./components/siteExhibitors/boothMaterialsTab/BoothMaterialsTab"));
const PromoBannerTab = lazy(
  () => import("./components/siteExhibitors/promoBannerTab/PromoBannerTab"),
);
const InquiriesTab = lazy(
  () => import("./components/siteExhibitors/inquiresTab/InquiriesTab"),
);
const MeetingsTab = lazy(() => import("./components/siteExhibitors/meetingsTab/MeetingsTab"));
const ExhibitorProfileTab = lazy(() => import("./components/siteExhibitors/ExhibitorProfileTab"));
const YourTickets = lazy(() => import("./components/siteExhibitors/YourTickets"));

const EventsLayout = lazy(() => import("./layouts/EventsLayout"));

// Helper component to redirect legacy /site/* URLs to root /*
const NavigateToRoot = () => {
  const { pathname, search, hash } = useLocation();
  const newPath = pathname.replace(/^\/site\/?/, "/");
  return <Navigate to={`${newPath}${search}${hash}`} replace />;
};

const EventDashboardRoute = ({ children }) => {
  const { slug } = useParams();
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const [isReady, setIsReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const { setEventId, setSiteSlug } = useBase();
  const { setEventData } = useSite();

  useEffect(() => {
    let isSubscribed = true;
    const startedAt = Date.now();

    accessDebug("EventResolver", "effect started", {
      slug,
      pathname,
      search,
      hash,
    });

    const resolveEvent = async () => {
      setLoadError(false);
      try {
        accessDebug("EventResolver", "resolving slug to event id", { slug });
        const res = await getEventId(slug);
        const data = res.data;
        accessDebug("EventResolver", "slug response received", {
          slug,
          status: res?.status,
          dataType: typeof data,
          rawEventId: data?.event_id || data?.id || data?.data?.event_id || data?.data?.id || data,
          latestSlug: data?.latestSlug || null,
          isSubscribed,
          durationMs: Date.now() - startedAt,
        });
        if (data && typeof data === "object" && data.latestSlug) {
          const nextPath = pathname.replace(`/dashboard/${slug}`, `/dashboard/${data.latestSlug}`);
          accessDebugWarn("EventResolver", "redirecting to latest slug", {
            slug,
            latestSlug: data.latestSlug,
            nextPath,
          });
          navigate(`${nextPath}${search}${hash}`, { replace: true });
          return;
        }
        const eventId = data?.event_id || data?.id || data?.data?.event_id || data?.data?.id || data;
        if (!eventId) {
          accessDebugWarn("EventResolver", "event id missing; redirecting to 404", {
            slug,
            responseDataType: typeof data,
          });
          navigate("/404", { replace: true });
          return;
        }
        const resolvedEventId = Number.isNaN(Number(eventId)) ? eventId : Number(eventId);
        accessDebug("EventResolver", "event id normalized", {
          slug,
          rawEventId: eventId,
          resolvedEventId,
          resolvedEventIdType: typeof resolvedEventId,
        });
        setDashboardContext({ eventId: resolvedEventId, eventSlug: slug });
        setEventId(resolvedEventId);
        setSiteSlug(slug);
        const userDetails = useAuth.getState().user;
        accessDebug("EventResolver", "requesting event details", {
          slug,
          eventId: resolvedEventId,
          hasUser: Boolean(userDetails),
          hasUserToken: Boolean(userDetails?.token),
        });
        const eventRes = await getEventData(resolvedEventId, userDetails?.token);
        const event = eventRes?.data?.event || eventRes?.data?.data?.event || eventRes?.data?.data || eventRes?.data;
        accessDebug("EventResolver", "event details response received", {
          slug,
          eventId: resolvedEventId,
          status: eventRes?.status,
          hasEvent: Boolean(event),
          responseRoleId: event?.role_id || null,
          eventSlug: event?.slug || event?.site_slug || null,
          isSubscribed,
          durationMs: Date.now() - startedAt,
        });
        if (event) {
          setEventData(event);
          if (event?.role_id) {
            accessDebug("EventResolver", "storing event role and preloading privileges", {
              slug,
              eventId: resolvedEventId,
              roleId: event.role_id,
            });
            setDashboardContext({ roleId: event.role_id });
            const privilegeResult = await fetchRolePrivileges(event.role_id, resolvedEventId);
            accessDebug("EventResolver", "privilege preload completed", {
              slug,
              eventId: resolvedEventId,
              roleId: event.role_id,
              hasResult: Boolean(privilegeResult),
              privilegeCount: privilegeResult?.data?.privileges?.length || 0,
              isSubscribed,
              durationMs: Date.now() - startedAt,
            });
          } else {
            accessDebugWarn("EventResolver", "event response has no role id; redirecting to access-denied", {
              slug,
              eventId: resolvedEventId,
            });
            setDashboardContext({ roleId: null });
            navigate("/access-denied", { replace: true });
            return;
          }
          if (isSubscribed) {
            accessDebug("EventResolver", "route marked ready", {
              slug,
              eventId: resolvedEventId,
              roleId: event?.role_id || null,
              durationMs: Date.now() - startedAt,
            });
            setIsReady(true);
          } else {
            accessDebugWarn("EventResolver", "ready state skipped: effect was cancelled", {
              slug,
              eventId: resolvedEventId,
            });
          }
        } else {
          accessDebugWarn("EventResolver", "event details missing; redirecting to 404", {
            slug,
            eventId: resolvedEventId,
          });
          navigate("/404", { replace: true });
        }
      } catch (err) {
        accessDebugError("EventResolver", "event request failed", err, {
          slug,
          durationMs: Date.now() - startedAt,
          isSubscribed,
        });
        if (!isSubscribed) return;
        if (err.response?.status === 404) {
          navigate("/404", { replace: true });
        } else if (err.response?.status === 403) {
          navigate("/access-denied", { replace: true });
        } else {
          setLoadError(true);
        }
      }
    };

    resolveEvent();

    return () => {
      isSubscribed = false;
      accessDebug("EventResolver", "effect cleanup", {
        slug,
        durationMs: Date.now() - startedAt,
      });
    };
  }, [slug, pathname, search, hash, navigate, setEventData, setEventId, setSiteSlug]);

  if (loadError) return <ConnectionRetry />;

  if (!isReady) {
    accessDebug("EventResolver", "rendering loading spinner", { slug, isReady });
    return <LoadingSpinner />;
  }
  accessDebug("EventResolver", "rendering dashboard children", { slug, isReady });
  return children;
};

const getFirstAllowedDashboardPath = (items = []) => {
  for (const item of items) {
    if (item?.tab_path) return item.tab_path;
    const subPath = getFirstAllowedDashboardPath(item?.subMenu || []);
    if (subPath) return subPath;
  }
  return "";
};

const EventDashboardHome = () => {
  const [targetPath, setTargetPath] = useState(null);
  const contextEventId = useAuth((state) => state.eventId);
  const contextRoleId = useAuth((state) => state.roleId);
  const currentRoleId = useAuth((state) => state.currentRoleId);
  const baseEventId = useBase((state) => state.eventId);
  const eventId = contextEventId || baseEventId;
  const roleId = contextRoleId || currentRoleId;

  useEffect(() => {
    let isSubscribed = true;
    accessDebug("DashboardHome", "target resolution started", {
      eventId,
      roleId,
    });

    const resolveTarget = async () => {
      if (!eventId || !roleId) {
        accessDebugWarn("DashboardHome", "target resolution missing context", {
          eventId,
          roleId,
        });
        if (isSubscribed) setTargetPath("");
        return;
      }

      const result = await fetchRolePrivileges(roleId, eventId);
      const privileges = result?.data?.privileges || [];
      const matchRows = privileges.map((item) => ({
        tabId: item?.tab_id,
        tabName: item?.tab_name,
        tabPath: item?.tab_path,
        matchesDashboard: matchesPrivilegePath(item?.tab_path, "/dashboard"),
      }));
      accessDebugTable("DashboardHome", "dashboard privilege checks", matchRows);
      const hasDashboardAccess = matchRows.some((item) => item.matchesDashboard);
      const firstAllowedPath = getFirstAllowedDashboardPath(privileges);
      accessDebug("DashboardHome", "target resolution completed", {
        eventId,
        roleId,
        hasResult: Boolean(result),
        privilegeCount: privileges.length,
        hasDashboardAccess,
        firstAllowedPath,
        isSubscribed,
      });
      if (isSubscribed) {
        setTargetPath(hasDashboardAccess ? "" : firstAllowedPath);
      }
    };

    resolveTarget();

    return () => {
      isSubscribed = false;
      accessDebug("DashboardHome", "target resolution cancelled", { eventId, roleId });
    };
  }, [eventId, roleId]);

  if (targetPath === null) {
    accessDebug("DashboardHome", "rendering loading spinner", { eventId, roleId });
    return <LoadingSpinner />;
  }
  if (targetPath) {
    const destination = toDashboardPath(targetPath);
    accessDebugWarn("DashboardHome", "redirecting to first allowed path", {
      eventId,
      roleId,
      targetPath,
      destination,
    });
    return <Navigate to={destination} replace />;
  }
  accessDebug("DashboardHome", "rendering dashboard through AuthGuard", {
    eventId,
    roleId,
  });
  // AuthenticatedLayout is provided by the parent dashboard shell, so only the
  // page + its guard render here (keeps the shared sidebar/header mounted).
  return (
    <AuthGuard>
      <Dashboard />
    </AuthGuard>
  );
};

// ---------------------------------------------------------------------------
// Shared layout shells.
// A layout route renders its chrome (AuthGuard / sidebar / header) ONCE and
// swaps only the matched child via <Outlet/>. Sidebar + header stay mounted
// across navigation instead of remounting (and reloading) on every route
// change. AuthGuard and AuthenticatedLayout keep re-checking auth/permissions
// off `useLocation`, so security behavior is unchanged.
// ---------------------------------------------------------------------------
const GuardedLayout = () => (
  <AuthGuard>
    <AuthenticatedLayout>
      <Outlet />
    </AuthenticatedLayout>
  </AuthGuard>
);

const PlainLayout = () => (
  <AuthenticatedLayout>
    <Outlet />
  </AuthenticatedLayout>
);

const EventsLayoutShell = () => (
  <AuthGuard>
    <EventsLayout>
      <Outlet />
    </EventsLayout>
  </AuthGuard>
);

// Resolves the event once per slug, then keeps the dashboard chrome mounted
// while sub-routes swap through <Outlet/>.
const EventDashboardShell = () => (
  <EventDashboardRoute>
    <Outlet />
  </EventDashboardRoute>
);

// AuthGuard placed BELOW AuthenticatedLayout so its permission check / redirect
// renders inside <main> (via <Outlet/>) instead of replacing the whole shell.
// The sidebar + header stay mounted across every guarded navigation.
const DashboardGuardShell = () => (
  <AuthGuard>
    <Outlet />
  </AuthGuard>
);

// Loading component for Suspense fallback
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="flex flex-col items-center space-y-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      <p className="text-gray-600 text-sm">Loading...</p>
    </div>
  </div>
);

const PwaHeadManager = () => {
  const location = useLocation();
  const { eventData } = useSite();

  const pathname = location.pathname;
  const isDashboard = pathname === '/dashboard' || pathname.startsWith('/dashboard/') || pathname.includes('/dashboard');

  let slug = '';
  if (!isDashboard) {
    const parts = pathname.split('/').filter(Boolean);
    if (parts.length > 0) {
      const firstPart = parts[0];
      const nonEventRoutes = [
        'login', 'signup', 'events', '404', 'privacy-policy', 'terms-and-conditions',
        'profile', 'forgot-password', 'reset-password', 'verify-otp', 'cancellation-policy',
        'refund-policy', 'contact-us', 'blogs', 'venue-list', 'halls', 'payment'
      ];
      if (!nonEventRoutes.includes(firstPart.toLowerCase())) {
        slug = firstPart;
      }
    }
  }

  usePwaManifest({
    eventSlug: isDashboard ? '' : slug,
    eventName: eventData?.event_name || eventData?.name || slug,
    logo: eventData?.logo || eventData?.image || '',
    themeColor: eventData?.theme_color || '#1A56DB',
    isDashboard: isDashboard,
  });

  return null;
};

function App() {
  const { fetchMe, user, isAuthLoaded, authLoadError } = useAuth();

  React.useEffect(() => {
    if (!user && !isAuthLoaded) {
      fetchMe();
    }
  }, [fetchMe, user, isAuthLoaded]);

  if (authLoadError && !user) {
    return <ConnectionRetry onRetry={() => fetchMe(true)} />;
  }

  return (
    <Router>
      <PwaHeadManager />
      <PwaInstallBanner />
      <Suspense fallback={<LoadingSpinner />}>
        <ScrollToTop />

        <Routes>
          {/* Public Routes (No AuthGuard) */}
          <Route
            path="/login"
            element={
              <RedirectGuard>
                <EventLogin />
              </RedirectGuard>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <RedirectGuard>
                <ForgotPassword />
              </RedirectGuard>
            }
          />
          <Route
            path="/reset-password"
            element={
              <RedirectGuard>
                <ResetPassword />
              </RedirectGuard>
            }
          />
          <Route
            path="/verify-otp"
            element={
              <RedirectGuard>
                <VerifyOtp />
              </RedirectGuard>
            }
          />
          <Route
            path="/signup"
            element={
              <RedirectGuard>
                <EventSignup />
              </RedirectGuard>
            }
          />
          <Route path="/access-denied" element={<AccessDenied />} />
          <Route path="/payment/checkout/:orderId" element={<PaymentCheckout />} />
          <Route path="/blogs" element={<BlogsHome />} />
          <Route path="/server-test" element={<LoadTestRunner />} />
          <Route path="/blog/:id" element={<BlogDetail />} />
          <Route path="/venue-listing" element={<VenueListPage />} />
          <Route
            path="/event-registration"
            element={<EventRegistrationPage />}
          />
          <Route path="/event-ticketing" element={<EventTicketing />} />
          <Route path="/check-in-badging" element={<CheckInBadging />} />
          <Route path="/get-in-touch" element={<GetInTouch />} />

          {/* Events list (AuthGuard + EventsLayout shared shell) */}
          <Route
            path="/events"
            element={<Navigate to="/dashboard" replace />}
          />
          <Route element={<EventsLayoutShell />}>
            <Route path="/dashboard" element={<EventsPage />} />
            <Route path="/dashboard/communication" element={<GlobalCommunication />} />
            <Route path="/payment-settings" element={<PaymentSettings />} />
            <Route path="/communication" element={<GlobalCommunication />} />
          </Route>

          {/* Standalone guarded routes (no shared chrome) */}
          <Route
            path="/payment-gateway-details"
            element={
              <AuthGuard>
                <PaymentGatewayDetails />
              </AuthGuard>
            }
          />
          <Route
            path="/profile"
            element={
              <AuthGuard>
                <Profile />
              </AuthGuard>
            }
          />

          {/* Top-level AuthGuard + AuthenticatedLayout (shared shell) */}
          <Route element={<GuardedLayout />}>
            <Route path="/manage/event-info" element={<EventInfoPage />} />
            <Route path="/manage/team" element={<Team />} />
            <Route path="/manage/speakers" element={<Speakers />} />
            <Route path="/manage/sponsors" element={<Sponsors />} />
            <Route path="/manage/sessions" element={<SessionsPage />} />
            <Route path="/manage/agenda/manage" element={<EventSchedule />} />
            <Route path="/manage/agenda" element={<EventSchedule />} />

            <Route path="/registration/ticket-class" element={<RegistrationTicketClass />} />
            <Route path="/registration/ticket-class/new" element={<TicketClassEditorPage scope="registration" />} />
            <Route path="/registration/ticket-class/edit/:ticketClassId" element={<TicketClassEditorPage scope="registration" />} />
            <Route path="/registration/master-ticket-class/new" element={<MasterTicketClassEditorPage scope="registration" />} />
            <Route path="/registration/master-ticket-class/edit/:groupId" element={<MasterTicketClassEditorPage scope="registration" />} />
            <Route path="/registration/payment/:tab?" element={<PaymentSettings />} />
            <Route path="/registration/register/:type/:ticketType?/:ticketName?" element={<OnSpotRegistration />} />
            <Route path="/registration/members-registration" element={<MembersRegistration />} />
            <Route path="/registration/pre-reg-groups" element={<DashboardPreRegGroups />} />
            <Route path="/registration/cancellation" element={<Cancellation />} />
            <Route path="/registration/import-export" element={<RegistrationImportExport />} />
            <Route path="/registration/:type?/:ticketType?" element={<AppliedRegistrations />} />

            <Route path="/speaker/ticket-class/new" element={<TicketClassEditorPage scope="speaker" />} />
            <Route path="/speaker/ticket-class/edit/:ticketClassId" element={<TicketClassEditorPage scope="speaker" />} />
            <Route path="/sponsor/ticket-class/new" element={<TicketClassEditorPage scope="sponsor" />} />
            <Route path="/sponsor/ticket-class/edit/:ticketClassId" element={<TicketClassEditorPage scope="sponsor" />} />

            <Route path="/exhibitor/ticket-class" element={<ExhibitorTicketClass />} />
            <Route path="/exhibitor/ticket-class/new" element={<TicketClassEditorPage scope="exhibitor" />} />
            <Route path="/exhibitor/ticket-class/edit/:ticketClassId" element={<TicketClassEditorPage scope="exhibitor" />} />
            <Route path="/exhibitor/master-ticket-class/new" element={<MasterTicketClassEditorPage scope="exhibitor" />} />
            <Route path="/exhibitor/master-ticket-class/edit/:groupId" element={<MasterTicketClassEditorPage scope="exhibitor" />} />
            <Route path="/exhibitor/ticketregistrations" element={<ExhibitorTicketRegistrations />} />

            <Route path="/private/ticket-class" element={<TeamTicketClass />} />
            <Route path="/private/ticket-class/new" element={<TicketClassEditorPage scope="team" />} />
            <Route path="/private/ticket-class/edit/:ticketClassId" element={<TicketClassEditorPage scope="team" />} />
            <Route path="/private/master-ticket-class/new" element={<MasterTicketClassEditorPage scope="team" />} />
            <Route path="/private/master-ticket-class/edit/:groupId" element={<MasterTicketClassEditorPage scope="team" />} />
            <Route path="/private/ticketregistrations" element={<TeamTicketRegistrations />} />

            <Route path="/promotion" element={<Promotion />} />
            <Route path="/event-day" element={<EventDayScreen />} />
          </Route>

          {/* Top-level AuthenticatedLayout only, no AuthGuard (shared shell) */}
          <Route element={<PlainLayout />}>
            <Route path="/access-manager" element={<AccessManagement />} />
            <Route path="/ticket/custom/:type/:id" element={<TicketCustomizerRoute />} />
            <Route
              path="/exhibitors/category"
              element={<><ExhibitorCategory /></>}
            />
            <Route
              path="/exhibitors/request-form"
              element={<><ExhibitorFormManager /></>}
            />
            <Route
              path="/exhibitors/requests"
              element={<><ExhibitorRequests /></>}
            />
            <Route
              path="/exhibitors/floor-plan"
              element={<><FloorPlan /></>}
            />
            <Route
              path="/exhibitors/booths"
              element={<><Booths /></>}
            />
            <Route
              path="/exhibitors/list"
              element={<><Exhibitors /></>}
            />
            <Route
              path="/exhibitors/booth-members"
              element={<><BoothMembers /></>}
            />
            <Route path="/exhibitor-summary" element={<ExhibitorSummary />} />
            <Route path="/exhibitor-settings" element={<ExhibitorSettings />} />
            <Route path="/whatsapp/broadcast" element={<WhatsApp />} />
            <Route path="/whatsapp/broadcast/edit" element={<RsvpEdit />} />
            <Route path="/whatsapp/broadcast/edit/:flowId" element={<RsvpEdit />} />
            <Route path="/communication/forms" element={<Forms />} />
            <Route path="/communication/forms/create" element={<CreateFormPage />} />
            <Route path="/communication/forms/edit/:formId" element={<CreateFormPage />} />
            <Route path="/forms" element={<Forms />} />
            <Route path="/forms/create" element={<CreateFormPage />} />
            <Route path="/forms/edit/:formId" element={<CreateFormPage />} />
            <Route path="/rsvp/flow" element={<RsvpFlowRedirect />} />
            <Route path="/rsvp/flow/edit" element={<RsvpFlowRedirect edit />} />
            <Route path="/rsvp/flow/edit/:flowId" element={<RsvpFlowRedirect edit />} />
            <Route path="/rsvp/accommodation" element={<RsvpAccommodation />} />
            <Route path="/rsvp/transportation" element={<RsvpTransportation />} />
            <Route path="/rsvp/import-export" element={<RsvpImportExport />} />
            <Route path="/rsvp/allotments" element={<RsvpAllotments />} />
            <Route path="/rsvp/analytics" element={<Navigate to="/analytics/accommodation" replace />} />
          </Route>

          {/* Event dashboard: resolve event once, keep chrome mounted across sub-routes */}
          <Route path="/dashboard/:slug" element={<EventDashboardShell />}>
            {/* AuthenticatedLayout mounts ONCE here (sidebar + header). Everything
                below swaps through <Outlet/> without remounting the chrome. */}
            <Route element={<PlainLayout />}>
              {/* Landing: keeps its own AuthGuard so it can redirect users without
                  dashboard access to their first allowed page (instead of 403). */}
              <Route index element={<EventDashboardHome />} />

              {/* Guarded pages share one AuthGuard, rendered inside <main>. */}
              <Route element={<DashboardGuardShell />}>
                <Route path="notifications" element={<Notifications />} />
              <Route path="contacts" element={<DashboardContact />} />
              <Route path="communication/:tab?" element={<><ToastContainer /><Communication /></>} />
              <Route path="whatsapp" element={<WhatsApp />} />
              <Route path="whatsapp/broadcast" element={<WhatsApp />} />
              <Route path="whatsapp/broadcast/edit" element={<RsvpEdit />} />
              <Route path="whatsapp/broadcast/edit/:flowId" element={<RsvpEdit />} />
              <Route path="communication/forms" element={<Forms />} />
              <Route path="communication/forms/create" element={<CreateFormPage />} />
              <Route path="communication/forms/edit/:formId" element={<CreateFormPage />} />
              <Route path="forms" element={<Forms />} />
              <Route path="forms/create" element={<CreateFormPage />} />
              <Route path="forms/edit/:formId" element={<CreateFormPage />} />
              <Route path="rsvp" element={<RsvpRedirect />} />
              <Route path="rsvp/flow" element={<RsvpFlowRedirect />} />
              <Route path="rsvp/flow/edit" element={<RsvpFlowRedirect edit />} />
              <Route path="rsvp/flow/edit/:flowId" element={<RsvpFlowRedirect edit />} />
              <Route path="rsvp/edit" element={<RsvpFlowRedirect edit />} />
              <Route path="rsvp/edit/:flowId" element={<RsvpFlowRedirect edit />} />
              <Route path="email" element={<><ToastContainer /><EmailSettings /></>} />
              <Route path="email/body" element={<><ToastContainer /><EmailBodyEdit /></>} />
              <Route path="affilate" element={<DashboardAffilate />} />
              <Route path="scan" element={<ScanBarcodeGun />} />
              <Route path="scan/:type/:id?" element={<SelectedScanPage />} />
              <Route path="analytics/sales/report" element={<TransactionReport />} />
              <Route path="analytics/:type?/:id?" element={<DashboardAnalytics />} />

              <Route path="manage/event-info" element={<EventInfoPage />} />
              <Route path="manage/team" element={<Team />} />
              <Route path="manage/speakers" element={<Speakers />} />
              <Route path="manage/sponsors" element={<Sponsors />} />
              <Route path="manage/sessions" element={<SessionsPage />} />
              <Route path="manage/agenda/manage" element={<EventSchedule />} />
              <Route path="manage/agenda" element={<EventSchedule />} />

              <Route path="registration/ticket-class" element={<RegistrationTicketClass />} />
              <Route path="registration/ticket-class/new" element={<TicketClassEditorPage scope="registration" />} />
              <Route path="registration/ticket-class/edit/:ticketClassId" element={<TicketClassEditorPage scope="registration" />} />
              <Route path="registration/ticket-class/duplicate/:ticketName/:ticketClassId" element={<TicketClassEditorPage scope="registration" />} />
              <Route path="registration/master-ticket-class/new" element={<MasterTicketClassEditorPage scope="registration" />} />
              <Route path="registration/master-ticket-class/edit/:groupId" element={<MasterTicketClassEditorPage scope="registration" />} />
              <Route path="registration/payment/:tab?" element={<PaymentSettings />} />
              <Route path="registration/register/:type/:ticketType?/:ticketName?" element={<OnSpotRegistration />} />
              <Route path="registration/members-registration" element={<MembersRegistration />} />
              <Route path="registration/pre-reg-groups" element={<DashboardPreRegGroups />} />
              <Route path="registration/cancellation" element={<Cancellation />} />
              <Route path="registration/import-export" element={<RegistrationImportExport />} />
              <Route path="registration/:type?/:ticketType?" element={<AppliedRegistrations />} />

              <Route path="speaker/ticket-class/new" element={<TicketClassEditorPage scope="speaker" />} />
              <Route path="speaker/ticket-class/edit/:ticketClassId" element={<TicketClassEditorPage scope="speaker" />} />
              <Route path="sponsor/ticket-class/new" element={<TicketClassEditorPage scope="sponsor" />} />
              <Route path="sponsor/ticket-class/edit/:ticketClassId" element={<TicketClassEditorPage scope="sponsor" />} />

              <Route path="exhibitor/ticket-class" element={<ExhibitorTicketClass />} />
              <Route path="exhibitor/ticket-class/new" element={<TicketClassEditorPage scope="exhibitor" />} />
              <Route path="exhibitor/ticket-class/edit/:ticketClassId" element={<TicketClassEditorPage scope="exhibitor" />} />
              <Route path="exhibitor/master-ticket-class/new" element={<MasterTicketClassEditorPage scope="exhibitor" />} />
              <Route path="exhibitor/master-ticket-class/edit/:groupId" element={<MasterTicketClassEditorPage scope="exhibitor" />} />
              <Route path="exhibitor/ticketregistrations" element={<ExhibitorTicketRegistrations />} />

              <Route path="private/ticket-class" element={<TeamTicketClass />} />
              <Route path="private/ticket-class/new" element={<TicketClassEditorPage scope="team" />} />
              <Route path="private/ticket-class/edit/:ticketClassId" element={<TicketClassEditorPage scope="team" />} />
              <Route path="private/master-ticket-class/new" element={<MasterTicketClassEditorPage scope="team" />} />
              <Route path="private/master-ticket-class/edit/:groupId" element={<MasterTicketClassEditorPage scope="team" />} />
              <Route path="private/ticketregistrations" element={<TeamTicketRegistrations />} />

              <Route path="access-manager" element={<AccessManagement />} />
              <Route path="exhibitors/category" element={<ExhibitorCategory />} />
              <Route path="exhibitors/request-form" element={<ExhibitorFormManager />} />
              <Route path="exhibitors/requests" element={<ExhibitorRequests />} />
              <Route path="exhibitors/floor-plan" element={<FloorPlan />} />
              <Route path="exhibitors/booths" element={<Booths />} />
              <Route path="exhibitors/list" element={<Exhibitors />} />
              <Route path="exhibitors/booth-members" element={<BoothMembers />} />
              <Route path="exhibitor-summary" element={<ExhibitorSummary />} />
              <Route path="exhibitor-settings" element={<ExhibitorSettings />} />
              <Route path="rsvp/accommodation" element={<RsvpAccommodation />} />
              <Route path="rsvp/transportation" element={<RsvpTransportation />} />
              <Route path="rsvp/import-export" element={<RsvpImportExport />} />
              <Route path="rsvp/allotments" element={<RsvpAllotments />} />
              <Route path="rsvp/analytics" element={<Navigate to="../analytics/accommodation" replace />} />

              <Route path="ticketScanner" element={<ScanTicket />} />
              <Route path="ticketScanner/scan" element={<DashboardScan />} />
              <Route path="ticketScanner/scan/:type/:id?" element={<SelectedScanPage />} />
              <Route path="ticketScanner/scanqr" element={<ScanQR />} />
              <Route path="attendee/verify" element={<ScanBarcode />} />
              <Route path="scan-gun" element={<ScanBarcodeGun />} />
              <Route path="checkin-checkout-confirmation" element={<CheckInCheckoutConfirmation />} />

              <Route path="hall-layouts" element={<Hall />} />
              <Route path="hall-listing" element={<HallListPage />} />
              <Route path="layout" element={<HallAnimation />} />
              <Route path="layout/:eventId/:hall" element={<HallAnimation />} />
              <Route path="hall-details/:id" element={<HallDetails />} />
              <Route path="zone-check-in" element={<ZoneCheckIn />} />
              </Route>

              {/* Unguarded pages: share the same AuthenticatedLayout, no AuthGuard */}
              <Route path="discounts/:type?" element={<Discounts />} />
              <Route path="ticket/custom/:type/:id" element={<TicketCustomizerRoute />} />
            </Route>

            {/* 404 without chrome (matches original DashboardScopedRoutes behavior) */}
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* Public Information Routes */}
          <Route path="/register" element={<AcceptInvitation />} />
          <Route path="/reject-invite" element={<RejectInvitation />} />
          <Route
            path="/register/reject/:token"
            element={<RejectInvitation />}
          />

          <Route path="/verify-ticket" element={<VerifyTicket />} />
          <Route path="/ticket/verify-ticket" element={<VerifyTicket />} />
          {/* <Route path="/subscribe" element={<Subscription />} /> */}
          <Route path="/" element={<Homepage />} />
          <Route path="/terms-condition" element={<TermsAndCondition />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/unsubscribe/:email" element={<UnsubscribeEmail />} />
          <Route path="/refund-cancellation" element={<Refunds />} />
          <Route path="/contact" element={<ContactUsPage />} />
          <Route path="/vendorvenue" element={<VendorVenueWeb />} />
          <Route path="/payment/status/:orderId" element={<PaymentStatus />} />
          <Route path="/schedule" element={<ScheduleManagement />} />
          <Route path="/hall" element={<HallAnimation />} />
          <Route path="/404" element={<NotFound />} />
          {/* Legacy Site URL Redirect */}
          <Route path="/site/*" element={<NavigateToRoot />} />

          {/* Event Site Routes */}
          <Route
            path="/:slug/dashboard"
            element={
              <SiteDataProvider>
                <SiteDashboard />
              </SiteDataProvider>
            }
          />
          <Route
            path="/:slug/your-tickets"
            element={
              <SiteDataProvider>
                <YourTickets />
              </SiteDataProvider>
            }
          />
          <Route
            path="/:slug/exhibitor/space"
            element={
              <SiteDataProvider>
                     <ToastContainer />
                <ExhibitorSpace />
              </SiteDataProvider>
            }
          >
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<OverviewTab />} />
            <Route path="booth-members" element={<BoothMembersTab />} />
            <Route path="booth-materials" element={<BoothMaterialsTab />} />
            <Route path="exhibitor-profile" element={<ExhibitorProfileTab />} />
            <Route path="promo-banner" element={<PromoBannerTab />} />
            <Route path="leads" element={<LeadsTab />} />
            <Route path="inquiries" element={<InquiriesTab />} />
            <Route path="meetings" element={<MeetingsTab />} />
            <Route path="*" element={<Navigate to="overview" replace />} />
          </Route>
          <Route
            path="/:slug/agenda"
            element={
              <SiteDataProvider>
                <SiteAgenda />
              </SiteDataProvider>
            }
          />
          <Route
            path="/:slug/terms"
            element={
              <SiteDataProvider>
                <SiteTerms />
              </SiteDataProvider>
            }
          />
          <Route
            path="/:slug/about"
            element={
              <SiteDataProvider>
                <SiteAbout />
              </SiteDataProvider>
            }
          />
          <Route
            path="/:slug/contact"
            element={
              <SiteDataProvider>
                <SiteContact />
              </SiteDataProvider>
            }
          />
          <Route
            path="/:slug/exhibitors"
            element={
              <SiteDataProvider>
                <ToastContainer />
                <SiteExhibitorsList />
              </SiteDataProvider>
            }
          />
          <Route
            path="/:slug/exhibitors/category"
            element={
              <SiteDataProvider>
                <ToastContainer />
                <SiteExhibitorCategory />
              </SiteDataProvider>
            }
          />
          <Route
            path="/:slug/exhibitors/profile/:exhibitor_id"
            element={
              <SiteDataProvider>
                <ToastContainer />
                <SiteExhibitorProfilePage />
              </SiteDataProvider>
            }
          />
          <Route
            path="/:slug/exhibitor-floor-plan/:eventId"
            element={
              <SiteDataProvider>
                 <ToastContainer />
                <SiteExhibitorFloorPlan />
              </SiteDataProvider>
            }
          />
          <Route
            path="/:slug/tickets/:type?/:ticketName?/:status?/:orderId?"
            element={
              <SiteDataProvider>
                <SiteTickets />
              </SiteDataProvider>
            }
          />
          <Route
            path="/:slug/forms/:formSlug"
            element={
              <SiteDataProvider>
                <PublicCustomForm />
              </SiteDataProvider>
            }
          />
          <Route
            path="/:slug/form/:formSlug"
            element={
              <SiteDataProvider>
                <PublicCustomForm />
              </SiteDataProvider>
            }
          />
          <Route
            path="/:slug?/:mode?"
            element={
              <SiteDataProvider>
                <EditSite />
              </SiteDataProvider>
            }
          />

          {/* Public Information Routes */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/refund-cancellation" element={<Refunds />} />
          <Route path="/contact" element={<ContactUsPage />} />
          <Route path="/vendorvenue" element={<VendorVenueWeb />} />
          <Route path="/payment/status/:orderId" element={<PaymentStatus />} />
          <Route path="/schedule" element={<><ToastContainer /><ScheduleManagement /></>} />

          {/* Fallback Route - For Non-existing Routes */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        <ToastContainer closeButton={false} closeOnClick={false} />
      </Suspense>
    </Router>
  );
}

export default App;
